/**
 * The staff panel.  CA-31 (React + Vite, ADR-001), replacing
 * prototype/admin.html + admin-live.js.
 *
 * The navigation is core's ONE list (admin-shell.NAV): ids, labels, order,
 * permissions and routes. A view the person cannot use is ABSENT from the
 * menu, not greyed out (core rule 20), and absent from the router too — a
 * typed-in address for it lands on Oversikt, with a toast that says why (PM gaps B8).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutDashboard, Clock, CalendarDays, Megaphone, Languages, Monitor,
  Image as ImageIcon, Inbox as InboxIcon, Users, KeyRound, BadgePercent, ScrollText,
  type LucideIcon,
  HandCoins, Banknote, ToggleRight, Archive, ShoppingBag, ListTodo, ListChecks, CalendarRange, Contact, Receipt, FolderOpen, Gavel, Vote, ClipboardList, ShieldCheck, Menu, Globe, Mic, MessageCircle, Target, Printer, Phone, Moon, Calculator, CircleDot,
} from 'lucide-react';
import { Shell, type NavItem } from './core';
import { API, can, call, health, setSession, send } from './lib/api';
import { useAuthEvent, useOffline, useSession } from './lib/hooks';
import { topDate } from './lib/format';
import { bannersToApprove, keepFresh } from './lib/refresh';
import { Nav } from './lib/nav';
import { focusables, trapStep } from './lib/focus-trap';
import { toast } from './lib/toast';
import { SignIn, Invite } from './components/SignIn';
import { Btn, Mark } from './components/ui';
import { NotifyPrefsDialog, mayChooseMail } from './components/NotifyPrefs';
import { MyAccountDialog } from './components/MyAccount';
import { exchangeVipps, LINK_ERROR, takeVippsReturn } from './lib/vipps-login';
import { Dashboard } from './views/Dashboard';
import { Times } from './views/Times';
import { Events } from './views/Events';
import { Notices } from './views/Notices';
import { Translations } from './views/Translations';
import { Screens } from './views/Screens';
import { Banners } from './views/Banners';
import { Inbox } from './views/Inbox';
import { Members } from './views/Members';
import { Access } from './views/Access';
import { Partners } from './views/Partners';
import { Audit } from './views/Audit';
import { Donations } from './views/Donations';
import { Butikk } from './views/Butikk';
import { Features } from './views/Features';
import { Retention } from './views/Retention';
import { Oppgaver } from './views/Oppgaver';
import { Sjekklister } from './views/Sjekklister';
import { Arshjul } from './views/Arshjul';
import { Kontakter } from './views/Kontakter';
import { Utlegg } from './views/Utlegg';
import { Innsamlinger } from './views/Innsamlinger';
import { Kontantgaver } from './views/Kontantgaver';
import { Budsjett } from './views/Budsjett';
import { Dokumenter } from './views/Dokumenter';
import { Styremoter } from './views/Styremoter';
import { Avstemninger } from './views/Avstemninger';
import { WhatsApp } from './views/WhatsApp';
import { Pamelding } from './views/Pamelding';
import { Personvern } from './views/Personvern';
import { Nettside } from './views/Nettside';
import { Kontaktinfo } from './views/Kontaktinfo';
import { Khutbah } from './views/Khutbah';
import { Tasbih } from './views/Tasbih';
import { Utskrifter } from './views/Utskrifter';
import { Ramadan } from './views/Ramadan';

const VIEWS: Record<string, React.ComponentType> = {
  dash: Dashboard, work: Oppgaver, checklists: Sjekklister, planner: Arshjul, contacts: Kontakter, expenses: Utlegg, documents: Dokumenter, board: Styremoter, voting: Avstemninger, times: Times, ramadan: Ramadan, khutbah: Khutbah, tasbih: Tasbih, events: Events, registration: Pamelding, notices: Notices, printables: Utskrifter, whatsapp: WhatsApp, trans: Translations, screens: Screens,
  banners: Banners, inbox: Inbox, members: Members, partners: Partners, donations: Donations, campaigns: Innsamlinger, store: Butikk, budget: Budsjett, cashGifts: Kontantgaver, access: Access, audit: Audit, features: Features, retention: Retention, privacy: Personvern, website: Nettside, contactPage: Kontaktinfo,
};
/**
 * The menu's icons.  Daniyal, 2026-09-20.
 *
 * Lucide, the same designed set the app now draws (apps/mobile Icon.tsx), in
 * core's NAV order. Every one of these was an emoji until 2026-09-20 — a
 * mosque, a calendar, a megaphone, a globe, a television and so on. An emoji
 * is a font glyph: it draws as a different picture on every operating system,
 * it grows with the text instead of staying 18px, and a screen reader reads it
 * aloud by whatever name it happens to carry (VoiceOver announces the
 * calendar one as "July 17"). The core spec now fails if one comes back.
 *
 * `partners` is the same BadgePercent the app uses for Medlemsfordeler: it is
 * the same thing seen from the two ends, and it should look like it.
 */
const ICON: Record<string, LucideIcon> = {
  dash: LayoutDashboard, work: ListTodo, checklists: ListChecks, planner: CalendarRange, contacts: Contact, expenses: Receipt, documents: FolderOpen, board: Gavel, voting: Vote, times: Clock, ramadan: Moon, khutbah: Mic, tasbih: CircleDot, events: CalendarDays, registration: ClipboardList, notices: Megaphone, printables: Printer, whatsapp: MessageCircle, trans: Languages,
  screens: Monitor, banners: ImageIcon, inbox: InboxIcon, members: Users, partners: BadgePercent,
  donations: HandCoins, campaigns: Target, store: ShoppingBag, budget: Calculator, cashGifts: Banknote, access: KeyRound, audit: ScrollText, features: ToggleRight, retention: Archive, privacy: ShieldCheck, website: Globe, contactPage: Phone,
};
const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Superadministrator', admin: 'Administrator', approver: 'Godkjenner', editor: 'Redaktør', volunteer: 'Frivillig',
  imam: 'Imam', board_member: 'Styremedlem', secretary: 'Sekretær', treasurer: 'Kasserer', board_chair: 'Styreleder',
};

/**
 * «Logg inn med Vipps» (2026-11-01): a return from Vipps (#/login?vippsCode=…, #/?vippsLink=…) is read and taken out of
 * the address once, when the panel loads — before the router reads the address, and before anything is spent.
 */
const VIPPS_RETURN = takeVippsReturn();
const VIPPS_LOGIN = VIPPS_RETURN && (VIPPS_RETURN.kind === 'login' || VIPPS_RETURN.kind === 'loginError') ? VIPPS_RETURN : null;
let vippsLinkPending = VIPPS_RETURN && (VIPPS_RETURN.kind === 'link' || VIPPS_RETURN.kind === 'linkError') ? VIPPS_RETURN : null;

/** The menu item the address belongs to — its own route or one under it (/medlemmer/register). A moved address
 *  (/medlemsbekreftelse) is rewritten in place to where it went, so a bookmark still lands on the right tab. */
function viewFromHash(): string {
  let route = '/';
  try { route = decodeURIComponent(location.hash.replace(/^#/, '').split('?')[0] || '/'); } catch { /* a broken address: the dashboard */ }
  const v = Shell.viewFor(route);
  if (v.moved) history.replaceState(null, '', '#' + v.route);
  return v.id;
}

export function App() {
  const session = useSession();
  const offline = useOffline();
  const auth = useAuthEvent();
  const [invite, setInvite] = useState<string | null>(() => new URLSearchParams(location.search).get('invite'));
  const [everIn, setEverIn] = useState(Boolean(session));
  const [view, setView] = useState(viewFromHash);
  const [badges, setBadges] = useState<Record<string, number>>({});
  const [today, setToday] = useState<string | null>(null);
  const dirty = useRef(new Set<string>());
  const [leaving, setLeaving] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  // The mosque this panel runs (ADR-004): its own short name, not a hard-coded CJAS.
  // Until /mosque answers: the product's neutral name (OA-016).
  const [mosqueShort, setMosqueShort] = useState('Masjid');
  // What the mosque HAS (/mosque `capabilities`), for views that show a capability's section only when it is on
  // (from platform-stage-1; the timetable source's card in Bønnetider is the first on main), and for nav items that need
  // a capability (Butikk), hidden until this says so.
  const [caps, setCaps] = useState<string[] | null>(null);
  // PM gaps B1 (2026-11-01): `cache: 'no-store'` — /mosque is served with five minutes' public cache (right for the app and
  // the screens), so a menu built from a cached answer kept a switched-on «Budsjett» away for up to five minutes. Asked
  // again by «Funksjoner» after every switch (refreshCaps in the Nav context).
  const refreshCaps = useCallback(async (): Promise<string[] | null> => {
    try {
      const r = await fetch(API + '/mosque', { cache: 'no-store' });
      const m = r.ok ? await r.json() : null;
      if (m && typeof m.shortName === 'string') { setMosqueShort(m.shortName); document.title = `${m.shortName} — Administrasjon`; }
      if (m && Array.isArray(m.capabilities)) { const list = m.capabilities.filter((c: unknown) => typeof c === 'string'); setCaps(list); return list; }
    } catch { /* offline: the name and the menu stay */ }
    return null;
  }, []);
  useEffect(() => { void refreshCaps(); }, [refreshCaps]);

  // A new staff member's link wins over any session left in this tab.
  useEffect(() => { if (invite) setSession(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (session) setEverIn(true); }, [session]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  useEffect(() => { void health().then((h) => h && setToday(h.now || null)); }, [session]);

  const refreshBadges = useCallback(async () => {
    if (!session) return;
    const st: { openRequests?: number; noticesAwaitingApproval?: number; membershipWork?: number; bannersAwaitingApproval?: number } = {};
    if (can('requests.read')) { try { st.openRequests = ((await call('/requests')).summary || {}).open || 0; } catch { /* leave it */ } }
    // Badges: open requests, and a janazah waiting for a SECOND person — the
    // one place minutes matter (fault 58). Only for someone who could approve.
    if (can('operations.read') && (can('notices.approve') || can('push.approve'))) { try { st.noticesAwaitingApproval = (await call('/notices')).awaitingApproval || 0; } catch { /* leave it */ } }
    // «Til behandling» under Medlemmer (2026-10-15): only what this person may act on (the server counts per viewer).
    if (can('members.link')) { try { st.membershipWork = (await call('/membership-work')).total || 0; } catch { /* leave it */ } }
    // QA C (2026-10-16): a banner waiting for this person's approval — only where the mosque has banners (every banner
    // route is a 404 without the capability) and the person may approve.
    if (caps && caps.includes('banners') && can('banners.approve')) { try { st.bannersAwaitingApproval = bannersToApprove((await call('/banners')).items); } catch { /* leave it */ } }
    setBadges(Shell.badges(st));
  }, [session, caps]);
  useEffect(() => { void refreshBadges(); }, [refreshBadges]);
  // Counted again every minute and when the window gets focus back (they were counted once, at sign-in).
  useEffect(() => keepFresh(() => { void refreshBadges(); }, window), [refreshBadges]);

  const navigate = useCallback((id: string) => {
    const item = Shell.NAV.find((n) => n.id === id);
    if (!item) return;
    location.hash = item.route === '/' ? '/' : item.route;
  }, []);
  const go = useCallback((id: string) => {
    const guard = Shell.canLeave(dirty.current.size > 0, id);
    if (!guard.ok) { setLeaving(id); return; }
    navigate(id);
  }, [navigate]);
  /*
   * UX audit U-15 (2026-10-19): on a phone the 64 px icon rail stayed, leaving ~311 px for the page — badges clipped,
   * the Medlemmer tabs cut, tables cut mid-word. Under 900 px (700 until the usability pass, 2026-10-29, which retired the
   * icon-only rail) the menu is a drawer behind «Meny» in the title bar,
   * with the names, the group headings and «Logg ut». Choosing a page, the scrim or Escape closes it.
   */
  /*
   * Review 2026-10-19: while it is open the drawer is modal — Tab stays inside it (lib/focus-trap) and the page behind
   * the scrim does not scroll under a finger; closing it gives the page its scroll back.
   */
  const [navOpen, setNavOpen] = useState(false);
  // «E-postvarsler» (2026-10-23): the popup with the person's own e-mail choices.
  const [notifyOpen, setNotifyOpen] = useState(false);
  // «Min konto» (2026-11-01): the person's own «Logg inn med Vipps» link.
  const [accountOpen, setAccountOpen] = useState(false);
  // Back from «Koble til Vipps»: the code is spent with this session (once), then Min konto opens with the result.
  useEffect(() => {
    const r = vippsLinkPending;
    if (!r) return;
    vippsLinkPending = null;
    // Signed out meanwhile: the code cannot be spent by anybody else's sign-in (review 2026-11-01). Said, and dropped.
    if (!session) { toast('Du var logget ut da du kom tilbake fra Vipps. Logg inn og koble til Vipps på nytt under Min konto.', 'warn'); return; }
    if (r.kind === 'linkError') { toast(LINK_ERROR[r.reason] || LINK_ERROR.failed, 'err'); setAccountOpen(true); return; }
    if (r.kind !== 'link') return;
    void exchangeVipps(r.code).then((x) => {
      toast(x.ok ? x.body.message_no || 'Vipps er koblet til kontoen din.' : (x.body.message_no || x.body.message || LINK_ERROR.failed), x.ok ? 'ok' : 'err');
      setAccountOpen(true);
    });
  }, [session]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setNavOpen(false); (document.querySelector('.menu-btn') as HTMLElement | null)?.focus(); return; }
      if (e.key !== 'Tab') return;
      const to = trapStep(focusables(document.getElementById('panel-nav')), document.activeElement as HTMLElement | null, e.shiftKey);
      if (to) { e.preventDefault(); to.focus(); }
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (document.querySelector('#panel-nav .nav button.on') as HTMLElement | null)?.focus();
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [navOpen]);
  useEffect(() => {
    const on = () => { setView(viewFromHash()); setNavOpen(false); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const setDirty = useCallback((key: string, d: boolean) => { if (d) dirty.current.add(key); else dirty.current.delete(key); }, []);
  useEffect(() => {
    const on = (e: BeforeUnloadEvent) => { if (dirty.current.size) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', on);
    return () => window.removeEventListener('beforeunload', on);
  }, []);

  // What this person may use (core rule 20) AND what the mosque has (a capability item, e.g. Butikk, while it is off).
  const visible = (n: NavItem) => Shell.permits(n, can) && Shell.capabilityAllows(n, caps);
  const nav = useMemo(() => Shell.NAV.filter(visible), [session, caps]); // eslint-disable-line react-hooks/exhaustive-deps
  // The same items, in the groups core defines. A group whose every item is
  // hidden from this person is not drawn at all (core rule 20).
  const groups = useMemo(() => Shell.navGroups(nav), [nav]);
  const current = nav.find((n) => n.id === view) || nav[0];
  /*
   * PM gaps B8 (2026-11-01): an address for a page this person cannot open used to land on Oversikt without a word — a
   * bookmark to «Budsjett» after it was switched off looked like a broken panel. Now it says why, and the address
   * becomes / so a reload does not ask again. A capability's page is judged only once /mosque has answered (if it never answers, the
   * address stays and Oversikt shows, as before).
   */
  useEffect(() => {
    if (!session) return;
    let route = '/';
    try { route = decodeURIComponent(location.hash.replace(/^#/, '').split('?')[0] || '/'); } catch { /* a broken address */ }
    if (route === '/' || (view !== 'dash' && nav.some((n) => n.id === view))) return;
    // viewFor() answers 'dash' for an address no item has.
    const item = view === 'dash' ? null : Shell.NAV.find((n) => n.id === view);
    let say = '';
    if (!item) say = 'Fant ikke den siden. Du er på Oversikt.';
    else if (!Shell.permits(item, can)) say = `Du har ikke tilgang til ${item.label_no} – spør en administrator.`;
    else if (caps === null) return;
    else say = `${item.label_no} er ikke slått på for moskeen.`;
    toast(say, 'warn', { source: 'hidden-route' });
    history.replaceState(null, '', '#/');
    setView('dash');
  }, [session, view, nav, caps]);
  const View = VIEWS[current.id];
  const warn = session ? Shell.sessionWarning({ expires_at: session.expiresAt }, now) : null;

  if (invite && !session) return <Invite token={invite} onDone={() => setInvite(null)} />;
  if (!session && !everIn) return <SignIn vipps={VIPPS_LOGIN} />;

  const guardMsg = Shell.canLeave(true);
  async function logout() { await send('/auth/logout', { method: 'POST' }); setSession(null); location.hash = '/'; location.reload(); }
  return (
    <Nav.Provider value={{ view: current.id, go, badges, refreshBadges, setDirty, today, caps, refreshCaps }}>
      <div className="wrap" {...(!session ? { inert: '' } : {})} aria-hidden={!session || undefined}>
        <aside className={'side' + (navOpen ? ' open' : '')} id="panel-nav">
          <div className="side-brand">
            <div className="m"><Mark /></div>
            <div><b>{mosqueShort}</b><span>Administrasjon</span></div>
          </div>
          {/* Grouped (Daniyal, 2026-09-20): twelve items in one undivided
              column is a list you read from the top every time. Each group is
              its own <ul> under a heading, so a screen reader announces "Folk,
              list, 3 items" rather than one list of twelve. The grouping is
              core's (Shell.navGroups) — the panel does not invent an order. */}
          <nav className="nav" aria-label="Hovedmeny">
            {groups.map((g) => (
              <div className="nav-group" key={g.id}>
                {g.label_no && <h2 className="nav-head" id={`navh-${g.id}`}>{g.label_no}</h2>}
                <ul {...(g.label_no ? { 'aria-labelledby': `navh-${g.id}` } : {})}>
                  {g.items.map((n) => {
                    const b = badges[n.id];
                    const Glyph = ICON[n.id];
                    // aria-label carries the count too, for a screen reader (A11Y-ADM-03).
                    return (
                      <li key={n.id}>
                        <button type="button" className={n.id === current.id ? 'on' : ''} title={n.label_no}
                                aria-label={n.label_no + (b ? `, ${b} ${n.id === 'inbox' ? 'åpne' : n.id === 'members' ? 'venter på deg' : 'venter'}` : '')}
                                aria-current={n.id === current.id ? 'page' : undefined} onClick={() => { setNavOpen(false); go(n.id); }}>
                          <span className="ico" aria-hidden="true">
                            {Glyph && <Glyph size={18} strokeWidth={n.id === current.id ? 2.3 : 1.9} aria-hidden="true" />}
                          </span>
                          <span>{n.label_no}</span>
                          {b ? <span className="badge" aria-hidden="true">{b}</span> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
          {session && (
            <div className="side-user">Innlogget som<br /><b>{session.user.name}</b> · {ROLE_LABELS[session.user.role] || session.user.role}
              <br /><span className="exp">Logges ut {new Date(session.expiresAt).toLocaleString('nb-NO', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</span>
              {/* E-postvarsler (owner, 2026-10-23): the person's own choices, only for someone who could be told anything. */}
              {mayChooseMail() && <>{' · '}<button type="button" className="link" onClick={() => { setNavOpen(false); setNotifyOpen(true); }}>E-postvarsler</button></>}
              {' · '}<button type="button" className="link" onClick={() => { setNavOpen(false); setAccountOpen(true); }}>Min konto</button>
              {' · '}<button type="button" className="link" onClick={logout}>Logg ut</button>
            </div>
          )}
        </aside>
        {session && <NotifyPrefsDialog open={notifyOpen} onClose={() => setNotifyOpen(false)} />}
        {session && <MyAccountDialog open={accountOpen} onClose={() => setAccountOpen(false)} />}
        {navOpen && <button type="button" className="nav-scrim" aria-label="Lukk menyen" onClick={() => setNavOpen(false)} />}
        <main className="main">
          <div className="top">
            <button type="button" className="menu-btn" aria-controls="panel-nav" aria-expanded={navOpen}
                    onClick={() => setNavOpen(!navOpen)}>
              <Menu size={20} strokeWidth={2} aria-hidden="true" /><span>Meny</span>
            </button>
            <h1 id="pageTitle">{current.label_no}</h1>
            <div className="sp" />
            {today && <span className="pill">{topDate(today)}</span>}
          </div>
          {offline && <div className="banner-bar err" role="alert">Får ikke kontakt med serveren. Det du ser kan være utdatert, og ingenting du lagrer nå kommer fram.</div>}
          {warn && warn.state === 'expiring' && <div className="banner-bar warn" role="status">{warn.message_no}</div>}
          {leaving && (
            <div className="banner-bar warn" role="alert">
              {guardMsg.message_no}{' '}
              <Btn danger onClick={() => { const to = leaving; dirty.current.clear(); setLeaving(null); navigate(to); }}>{guardMsg.confirmLabel_no}</Btn>{' '}
              <Btn ghost onClick={() => setLeaving(null)}>{guardMsg.cancelLabel_no}</Btn>
            </div>
          )}
          <div className="content">
            <section className="view on" id={'v-' + current.id} aria-labelledby="pageTitle"><View key={current.id} /></section>
          </div>
        </main>
      </div>
      {!session && everIn && <SignIn note={auth && auth.kind === 'expired' ? 'Økten er utløpt. Logg inn på nytt.' : null} />}
    </Nav.Provider>
  );
}
